import { createComponentStore } from '../../utils/createComponentStore';

// ---- Utility functions (module-scope, exported) ----

export const keyAuto = () =>
  'conditional' + (Math.floor(Math.random() * 90000000) + 10000).toString();

export const logicData = () => ({
  key: keyAuto(),
  keyRandom: Math.random(),
  outputType: 'TEXT',
  statement: {
    type: 'IF',
    content: {
      condition: [
        {
          operators: [
            { logic: 'undefined', key: '', function: 'EQUAL', value: '' },
          ],
          then: { statement: { type: 'VALUE', content: '' } },
        },
        {
          then: { statement: { type: 'VALUE', content: '' } },
        },
      ],
    },
  },
});

// ---- Store creation via factory ----

const initData = () => [logicData()];

const { getStore, defaultStore } = createComponentStore({
  type: 'conditional_logic',
  endpoint: 'conditional-logic',
  registryKey: 'conditional-logic',
  initData,
  onLoadData: (rawData) => (Array.isArray(rawData) ? rawData : initData()),

  extraState: () => ({
    placeholders: [],
    placeholdersError: [],
    newData: [],
    positions: {
      indexLogic: null,
      indexStatement: null,
      indexCondition: null,
      type: null,
    },
  }),

  extraActions: (set, get) => ({
    // ---- Backward-compat aliases ----

    setConditionalLogicTemplate: (template) => {
      // Backward-compat: wraps raw template into the standard envelope
      const component = {
        componentId: get().componentId,
        type: 'conditional_logic',
        componentData: template,
      };
      get().setComponentData(component);
    },

    setConditionalLogicName: (name) => get().setName(name),

    // ---- Condition / statement mutations ----

    setCondition: (content, indexLogic, indexStatement, indexCondition, type) => {
      try {
        const data = JSON.parse(JSON.stringify(get().data));
        switch (type) {
          case 'logic':
            data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].logic = content;
            break;
          case 'key':
            data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].key = content ? content.trim() : '';
            break;
          case 'function':
            data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].function = content;
            break;
          case 'value':
            data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition].value = content;
            break;
        }
        set({ data });
      } catch (error) {
        console.error(error);
      }
    },

    setThenContent: (content, indexLogic, indexStatement) => {
      try {
        const data = JSON.parse(JSON.stringify(get().data));
        data[indexLogic].statement.content.condition[indexStatement].then.statement.content = content;
        set({ data });
      } catch (error) {
        console.error(error);
      }
    },

    addStatement: (indexLogic) => {
      const obj = {
        operators: [{ logic: 'OR', key: '', function: '', value: '' }],
        then: { statement: { type: 'VALUE', content: '' } },
      };
      const data = JSON.parse(JSON.stringify(get().data));
      const { condition } = data[indexLogic].statement.content;
      data[indexLogic].statement.content.condition.splice(condition.length - 1, 0, obj);
      set({ data });
    },

    addCondition: (indexLogic, indexStatement) => {
      const obj = {
        conditionRandomKey: Math.random(),
        logic: 'OR',
        key: '',
        function: 'EQUAL',
        value: '',
      };
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition[indexStatement].operators.push(obj);
      set({ data });
    },

    setLogic: (content, index, type) => {
      const data = JSON.parse(JSON.stringify(get().data));
      switch (type) {
        case 'key':
          data[index].key = content;
          break;
        case 'outputType':
          data[index].outputType = content;
          break;
      }
      set({ data });
    },

    addLogic: () => {
      const obj = logicData();
      const data = [...get().data, obj];
      const newData = [...get().newData, obj.keyRandom];
      set({ data, newData });
    },

    clearData: () => set({ data: [], newData: [] }),

    removeLogic: (key) => {
      const data = get().data.filter((x) => x.key !== key);
      set({ data });
    },

    newRemoveLogic: (key) => {
      const data = get().data.filter((x) => x.keyRandom !== key);
      set({ data });
    },

    copyThisLogic: (logic) => {
      const obj = {
        ...JSON.parse(JSON.stringify(logic)),
        key: keyAuto(),
        keyRandom: Math.random(),
      };
      set({ data: [...get().data, obj] });
    },

    copyThisCondition: (indexLogic, indexStatement, indexCondition) => {
      const data = JSON.parse(JSON.stringify(get().data));
      const operator = data[indexLogic].statement.content.condition[indexStatement].operators[indexCondition];
      const copyOperator = JSON.parse(JSON.stringify(operator));
      data[indexLogic].statement.content.condition[indexStatement].operators.push(copyOperator);
      set({ data });
    },

    copyThisStatement: (indexLogic, indexStatement) => {
      const data = JSON.parse(JSON.stringify(get().data));
      const statement = data[indexLogic].statement.content.condition[indexStatement];
      const copyStatement = JSON.parse(JSON.stringify(statement));
      const { condition } = data[indexLogic].statement.content;
      data[indexLogic].statement.content.condition.splice(condition.length - 1, 0, copyStatement);
      set({ data });
    },

    removeCondition: (indexLogic, indexStatement, indexCondition) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition[indexStatement].operators.splice(indexCondition, 1);
      set({ data });
    },

    handleRemoveCondition: (indexLogic, indexStatement, indexCondition) => {
      const data = JSON.parse(JSON.stringify(get().data));
      const idx = data[indexLogic].statement.content.condition[indexStatement].operators.findIndex(
        (x) => x.conditionRandomKey === indexCondition
      );
      data[indexLogic].statement.content.condition[indexStatement].operators.splice(idx, 1);
      set({ data });
    },

    removeStatement: (indexLogic, indexStatement) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data[indexLogic].statement.content.condition.splice(indexStatement, 1);
      set({ data });
    },

    setPlaceholders: (placeholders) => set({ placeholders }),

    setPositions: (positions) => set({ placeholders: [], positions }),

    // ---- Override getTemplate to include extra UI fields ----

    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        type: s.type,
        lastModified: s.lastModified,
        data: JSON.parse(JSON.stringify(s.data)),
        newData: s.newData,
        placeholders: s.placeholders,
        positions: s.positions,
      };
    },
  }),
});

// ---- Exports ----

export { getStore as getConditionalLogicStore };
export default defaultStore;
